const getAuthHeader = () => {
  const token = localStorage.getItem('authToken');
  if (token) {
    return { 'Authorization': `Bearer ${token}` };
  }
  return {};
};

const API_CONFIG = {
  headers: {
    'Content-Type': 'application/json',
  },
  // credentials: 'include', // credentials can be managed per request if needed, or by handleApiResponse
};

const RETRYABLE_STATUSES = [502, 503, 504];
const MAX_RETRIES = 3;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// redirectOn401: false — 401 не считается истёкшей сессией (вход: это неверный логин или пароль)
const handleApiResponse = async (url, options, { redirectOn401 = true } = {}) => {
  const requestOptions = {
    ...options,
    headers: {
      ...API_CONFIG.headers,
      ...options.headers, // Allow overriding Content-Type or adding other headers like Authorization
    },
    credentials: options.credentials || 'include', // Default to include credentials
  };

  let lastResponse = null;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const response = await fetch(url, requestOptions);
    lastResponse = response;

    if (response.ok) {
      break;
    }

    if (response.status === 401) {
      break;
    }

    if (RETRYABLE_STATUSES.includes(response.status) && attempt < MAX_RETRIES) {
      await sleep(1000 * Math.pow(2, attempt));
      continue;
    }

    break;
  }

  const response = lastResponse;

  if (response.status === 401 && redirectOn401) {
    console.error('API request unauthorized (401). Token might be expired or invalid.');
    localStorage.removeItem('authToken'); // Clear token
    // Potentially dispatch a custom event for logout if a global handler is set up in React
    // window.dispatchEvent(new CustomEvent('auth-error-logout'));
    window.location.href = '/login?reason=expired'; // Redirect to login with reason
    throw new Error('Сессия истекла или недействительна. Пожалуйста, войдите снова.');
  }

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch (e) {
      // If response is not JSON, use statusText
      errorData = { message: response.statusText };
    }
    console.error(`API Error ${response.status}:`, errorData);
    throw new Error(errorData.message || errorData.error || `Ошибка сервера: ${response.status}`);
  }

  // If response is OK, try to parse JSON. Handle cases where response might be empty.
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.indexOf("application/json") !== -1) {
    return await response.json();
  }
  // If not JSON, or no content, return null or handle as appropriate for your app
  // For example, for a 204 No Content response, you might want to return a specific success indicator
  if (response.status === 204) {
    return { success: true, message: 'Operation successful, no content returned.' };
  }
  return await response.text(); // Or handle non-JSON responses differently
};

/**
 * API service object for handling API requests
 */
export const ApiServices = {
  /**
   * Get hot trends for the HomePage (latest 6, from DB only)
   * 
   * @param {Object} params - Query parameters
   * @param {string} params.geo - Country code to filter by
   * @param {number} params.limit - Max trends to return (default 6)
   * @returns {Promise<Object>} Hot trends object
   */
  getHotTrends: async (params = {}) => {
    const geo = params.geo === 'all' ? null : params.geo;
    const limit = params.limit || 6;
    const url = geo ? `/api/core/trends/hot/?geo=${geo}&limit=${limit}` : `/api/core/trends/hot/?limit=${limit}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Get top celebrities for the HomePage (Celebrity trends of the last 48 hours, one item per person, from DB only)
   *
   * @param {Object} params - Query parameters
   * @param {string} params.geo - Country code to filter by ('all' for the whole world)
   * @param {number} params.limit - Max celebrities to return (default 10)
   * @returns {Promise<Object>} { celebrities: [...] }, each item has geos (countries, the most searched first)
   */
  getHotCelebrities: async (params = {}) => {
    const geo = params.geo === 'all' ? null : params.geo;
    const limit = params.limit || 10;
    const url = geo ? `/api/core/celebrities/hot/?geo=${geo}&limit=${limit}` : `/api/core/celebrities/hot/?limit=${limit}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * "Who is this" card of a person from trends: photo, birth date, what they are known for
   *
   * @param {string} name - Trend name (the person)
   * @param {string} geo - Trend country, helps not to mix up namesakes
   * @returns {Promise<Object>} { status, profile }; status 'pending' - the card is being prepared, ask again in a few seconds
   */
  getCelebrityProfile: async (name, geo) => {
    const params = new URLSearchParams({ name });
    if (geo && geo !== 'all') params.append('geo', geo);
    return handleApiResponse(`/api/core/celebrities/profile/?${params.toString()}`, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Sort data based on provided criteria
   * 
   * @param {Object} data - Data containing sort parameters
   * @param {string} data.sortBy - Field to sort by
   * @param {string} data.order - Order direction ('asc' or 'desc')
   * @returns {Promise<Object>} Sorted data response
   */
  sortData: async (data) => {
    return handleApiResponse('/api/core/sort/', {
      method: 'POST',
      headers: getAuthHeader(), // Auth header passed here
      body: JSON.stringify(data),
    });
  },

  /**
   * Get a list of items with optional filtering
   * 
   * @param {Object} params - Query parameters
   * @param {string} params.sortBy - Field to sort by
   * @param {string} params.order - Order direction
   * @param {boolean} params.activeOnly - Only include active items
   * @param {number} params.category - Category ID filter
   * @returns {Promise<Array>} List of items
   */
  trackAuditEvent: async (eventType, section = null) => {
    try {
      await handleApiResponse('/api/core/audit/track', {
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify({ event_type: eventType, section }),
      });
    } catch (error) {
      console.error('Failed to track audit event:', error);
    }
  },

  getAuditLogs: async (params = {}) => {
    const queryString = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== '')
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');
    const url = `/api/core/audit/logs${queryString ? `?${queryString}` : ''}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  getItems: async (params = {}) => {
    const queryString = Object.entries(params)
      .filter(([_, value]) => value !== undefined)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');
    const url = `/api/core/items/${queryString ? `?${queryString}` : ''}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Get detailed information about a specific item
   * 
   * @param {number} itemId - ID of the item to retrieve
   * @returns {Promise<Object>} Item details
   */
  getItemDetail: async (itemId) => {
    const url = `/api/core/items/${itemId}/`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Create a new item
   * 
   * @param {Object} itemData - Data for the new item
   * @returns {Promise<Object>} Created item data
   */
  createItem: async (itemData) => {
    return handleApiResponse('/api/core/items/create/', {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(itemData),
    });
  },

  /**
   * Update an existing item
   * 
   * @param {number} itemId - ID of the item to update
   * @param {Object} itemData - Updated item data
   * @returns {Promise<Object>} Updated item data
   */
  updateItem: async (itemId, itemData) => {
    const url = `/api/core/items/${itemId}/update/`;
    return handleApiResponse(url, {
      method: 'PUT',
      headers: getAuthHeader(),
      body: JSON.stringify(itemData),
    });
  },

  /**
   * Delete an item
   * 
   * @param {number} itemId - ID of the item to delete
   * @returns {Promise<Object>} Deletion confirmation
   */
  deleteItem: async (itemId) => {
    const url = `/api/core/items/${itemId}/delete/`;
    return handleApiResponse(url, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
  },

  /**
   * Export data
   * 
   * @param {Object} exportData - Export parameters
   * @param {string} exportData.dataType - Type of data to export
   * @param {Object} exportData.filters - Filters to apply
   * @returns {Promise<Object>} Export task information
   */
  exportData: async (exportData) => {
    return handleApiResponse('/api/data/export/', {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(exportData),
    });
  },

  /**
   * Check export status
   * 
   * @param {number} exportId - ID of the export to check
   * @returns {Promise<Object>} Export status information
   */
  getExportStatus: async (exportId) => {
    const url = `/api/data/export/${exportId}/status/`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Authenticate user (login) via JWT endpoint
   * @param {Object} credentials - { username, password }
   * @returns {Promise<Object>} Auth response, expects { access_token: '...' } on success
   */
  login: async ({ username, password }) => {
    // Login doesn't need auth header, but still benefits from unified response handling
    // It might not throw 401 in the same way, but can still error.
    try {
      // 401 на входе — неверный логин или пароль: сообщение остаётся на форме, страница не перезагружается
      const data = await handleApiResponse('/api/core/auth/login', {
        method: 'POST',
        // No getAuthHeader() needed for login itself
        body: JSON.stringify({ username, password }),
      }, { redirectOn401: false });
      // Expect access_token on success (if using JWT), otherwise just return data
      if (data.access_token === undefined && !data.message) {
        throw new Error('Вход выполнен, но не получен токен или подтверждение.');
      }
      return data; // Return the whole data object
    } catch (error) {
      throw new Error(error.message || 'Ошибка входа. Пожалуйста, проверьте свои данные.');
    }
  },

  /**
   * Get trending searches based on specified parameters
   * 
   * @param {Object} params - Query parameters
   * @param {string} params.geo - Geographic region code (empty for worldwide)
   * @param {string} params.date - Date range for trends e.g. 'now 1-d'
   * @param {number} params.page - Page number for pagination
   * @param {number} params.page_size - Number of items per page
   * @param {string} params.category - Optional category filter
   * @param {string} params.domain - Optional domain filter
   * @returns {Promise<Object>} Trends data
   */
  getTrends: async (params = {}) => {
    const adjustedParams = {
      geo: params.geo || 'all',
      date: params.date || 'now 1-d',
      page: params.page || 1,
      page_size: params.page_size || 10,
      category: params.category,
      domain: params.domain
    };

    if (params.force_serpapi) {
      adjustedParams.force_serpapi = '1';
    }
    if (params.only_with_correlation) {
      adjustedParams.only_with_correlation = '1';
    }

    const queryString = Object.entries(adjustedParams)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    const url = `/api/core/trends?${queryString}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Fetch all trends: DB-first (like Homepage). If geo has no data in DB,
   * returns { trends: [], geo_has_no_data: true } so caller can trigger SerpAPI fetch.
   * @param {Object} params - Same as getTrends
   * @param {number} initial_page_size - Max trends to fetch
   * @returns {Promise<{trends: Array, source?: string, geo_has_no_data?: boolean}>}
   */
  fetchAllTrends: async (params = {}, initial_page_size = 500) => {
    const extractTrends = (data) => {
      if (!data) return [];
      if (data.trends && Array.isArray(data.trends)) return data.trends;
      if (data.interest_over_time?.timeline_data) {
        let list = [];
        data.interest_over_time.timeline_data.forEach((tp) => {
          if (tp.values && Array.isArray(tp.values)) list = list.concat(tp.values);
        });
        return list;
      }
      if (Array.isArray(data)) return data;
      if (data.trending_searches) return data.trending_searches;
      return [];
    };

    const dedupeByQuery = (trends) => {
      const seen = new Set();
      return trends.filter((t) => {
        const q = t.query ?? t.title ?? '';
        if (!q || seen.has(q)) return false;
        seen.add(q);
        return true;
      });
    };

    try {
      const data = await ApiServices.getTrends({
        ...params,
        page: 1,
        page_size: initial_page_size
      });

      if (data?.geo_has_no_data === true) {
        return {
          trends: [],
          source: 'db',
          geo_has_no_data: true
        };
      }

      const allTrends = extractTrends(data);
      const uniqueTrends = dedupeByQuery(allTrends);
      return {
        trends: uniqueTrends,
        source: data?.source || 'db',
        geo_has_no_data: false
      };
    } catch (error) {
      return { trends: [], source: 'error', geo_has_no_data: false };
    }
  },

  /**
   * Start asynchronous research for a query.
   * Returns cached result if available, unless force=true.
   * @param {string} query - The search query
   * @param {{ force?: boolean }} options - force=true skips cache and runs new research
   * @returns {Promise<Object>} Object with task_id or from_cache+content+citations
   */
  startResearch: async (query, options = {}) => {
    const params = new URLSearchParams();
    if (options.force) params.set('force', '1');
    if (options.geo) params.set('geo', options.geo);
    const qs = params.toString();
    const url = `/api/core/research-async/${encodeURIComponent(query)}${qs ? `?${qs}` : ''}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Get the status of a research task
   * @param {string} taskId - The ID of the task
   * @returns {Promise<Object>} Task status object { status, progress_message?, content?, citations?, error? }
   */
  getResearchStatus: async (taskId) => {
    const url = `/api/core/research-status/${taskId}`;
    try {
      // Special handling for 404 in getResearchStatus as it might mean task is pending
      const response = await fetch(url, {
        method: 'GET',
        headers: { ...API_CONFIG.headers, ...getAuthHeader() },
        credentials: 'include',
      });
      if (response.status === 401) { // Explicitly handle 401 here too for consistency
        localStorage.removeItem('authToken');
        window.location.href = '/login';
        throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
      }
      if (response.status === 404) {
        return { status: 'pending', progress_message: 'Задача еще не найдена...' };
      }
      // Treat transient gateway errors as temporary and keep polling
      if (RETRYABLE_STATUSES.includes(response.status)) {
        return {
          status: 'pending',
          progress_message: `Временная ошибка связи (${response.status}). Продолжаем ожидание результата...`,
        };
      }
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(`Error ${response.status}: ${errorData.message || 'Не удалось получить статус исследования'}`);
      }
      return await response.json();
    } catch (error) {
      // Network-level errors: treat as transient and keep polling instead of failing with 504, etc.
      if (error instanceof TypeError) {
        return {
          status: 'pending',
          progress_message: 'Временная сетевая ошибка. Пробуем еще раз получить статус...',
        };
      }
      // For non-network errors, surface them to the UI
      return { status: 'error', error: error.message || 'Не удалось получить статус' };
    }
  },

  /**
   * Fetch detailed data for a specific trend
   * @param {string} query - The trend query string
   * @param {Object} params - Optional query parameters
   * @param {string} params.date - Date range for trend data
   * @param {string} params.geo - Geographic region code
   * @returns {Promise<Object>} Trend detail data
   */
  getTrendDetail: async (query, params = {}) => {
    // The `query` is the base trend string, e.g., "yankees vs dodgers"
    // The `params` object contains `date` and `geo` from the page's state.
    // Use proper URL structure: path segment for trend name + query string for params.

    const encodedQuery = encodeURIComponent(query);
    const searchParams = new URLSearchParams();
    if (params.date) searchParams.append('date', params.date);
    if (params.geo) searchParams.append('geo', params.geo);
    const queryString = searchParams.toString();
    const url = `/api/core/trend/${encodedQuery}${queryString ? `?${queryString}` : ''}`;
    try {
      const data = await handleApiResponse(url, {
        method: 'GET',
        headers: getAuthHeader(),
      });

      // Ensure related_topics and related_queries exist with proper structure (as per original ApiService)
      if (!data.related_topics) {
        data.related_topics = { rising: [] };
      } else if (!data.related_topics.rising) {
        data.related_topics.rising = [];
      }

      if (!data.related_queries) {
        data.related_queries = { rising: [] };
      } else if (!data.related_queries.rising) {
        data.related_queries.rising = [];
      }

      return data;
    } catch (error) {
      console.error("Error fetching trend details:", error);
      // Ensure a consistent error structure for the caller
      return {
        error: error.message || 'Failed to fetch trend details',
        interest_over_time: { timeline_data: [] },
        related_topics: { rising: [] },
        related_queries: { rising: [] }
      };
    }
  },

  /**
   * Fetch detailed data for multiple trends in a single batch request
   * @param {Array<string>} queries - Array of trend query strings (max 10)
   * @param {Object} params - Optional query parameters
   * @param {string} params.date - Date range for trend data (default: 'now 7-d')
   * @param {string} params.geo - Geographic region code (default: 'US')
   * @returns {Promise<Object>} Batch results { results: { query: data }, errors: { query: error } }
   */
  getTrendDetailsBatch: async (queries, params = {}) => {
    if (!queries || !Array.isArray(queries) || queries.length === 0) {
      return { results: {}, errors: { general: 'No queries provided' } };
    }

    // Limit to 10 queries
    const limitedQueries = queries.slice(0, 10);

    const requestBody = {
      queries: limitedQueries,
      date: params.date || 'now 7-d',
      geo: params.geo || 'US',
      aggregate_daily: params.aggregate_daily !== false // default true, can be overridden
    };

    try {
      const data = await handleApiResponse('/api/core/trends/batch-details', {
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify(requestBody),
      });

      return data;
    } catch (error) {
      console.error('Error fetching batch trend details:', error);
      return {
        results: {},
        errors: { general: error.message || 'Failed to fetch batch trend details' }
      };
    }
  },

  /**
   * Change user password
   * @param {Object} data - { currentPassword, newPassword }
   * @returns {Promise<Object>} Success message or error
   */
  changePassword: async ({ currentPassword, newPassword }) => {
    // Use the newly created api_update_user_profile endpoint
    // Mapping fields to what update_user expects
    return handleApiResponse('/api/core/users/me', {
      method: 'PUT',
      headers: getAuthHeader(),
      body: JSON.stringify({
        current_password: currentPassword,
        password: newPassword
      }),
    });
  },

  /**
   * Get list of users (Admin/Lead only)
   * @param {Object} params - Query params like page, page_size, search
   * @returns {Promise<Object>} users list and count
   */


  /**
   * Get research results for a completed research task
   * @param {string} taskId - The task ID
   * @returns {Promise<Object>} Research results
   */
  getResearchResults: async (taskId) => {
    const url = `/api/core/research/results/${taskId}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Get the status of a trend classification task
   * @param {string} taskId - The ID of the classification task
   * @returns {Promise<Object>} Task status object { task_id, status, message, updated_at }
   */
  getTrendClassificationStatus: async (taskId) => {
    const url = `/api/core/trend-classification-status/${taskId}`;
    // This function is similar to getResearchStatus but points to a different endpoint
    // and might have a slightly different expected response structure or error handling.
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { ...API_CONFIG.headers, ...getAuthHeader() },
        credentials: 'include', // Assuming credentials are still needed
      });
      // Handle 401 Unauthorized consistently
      if (response.status === 401) {
        localStorage.removeItem('authToken');
        window.location.href = '/login?reason=session_expired_classification_status';
        throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
      }
      // Handle 404 Not Found - classification task might not exist or not yet processed
      if (response.status === 404) {
        return {
          task_id: taskId,
          status: 'not_found', // Or 'pending' if appropriate based on backend logic for unknown tasks
          message: 'Задача классификации не найдена или еще не обработана.',
          updated_at: new Date().toISOString()
        };
      }
      // Handle other non-OK responses
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        console.error(`Error fetching classification status ${response.status}:`, errorData);
        throw new Error(errorData.message || `Не удалось получить статус классификации: ${response.status}`);
      }
      // Parse and return JSON response
      return await response.json();
    } catch (error) {
      console.error('Error in getTrendClassificationStatus:', error);
      // Return a consistent error structure for the polling logic
      return {
        task_id: taskId,
        status: 'error',
        message: error.message || 'Ошибка при получении статуса классификации.',
        updated_at: new Date().toISOString()
      };
    }
  },

  // ===== PROMPTING TEMPLATES API =====

  /**
   * Get user's prompt templates
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Number of templates to return
   * @param {number} params.offset - Offset for pagination
   * @returns {Promise<Object>} Templates response
   */
  getPromptTemplates: async (params = {}) => {
    const queryString = Object.entries(params)
      .filter(([_, value]) => value !== undefined)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');
    const url = `/api/prompting/templates/${queryString ? `?${queryString}` : ''}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Get a single prompt template by ID
   * @param {number} templateId - Template ID
   * @returns {Promise<Object>} Template data
   */
  getPromptTemplate: async (templateId) => {
    const url = `/api/prompting/templates/${templateId}/`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Create a new prompt template
   * @param {Object} templateData - Template data
   * @param {string} templateData.name - Template name (required)
   * @param {string} templateData.description - Template description (optional)
   * @param {string} templateData.method - Prompting method (required)
   * @param {string} templateData.prompt - Template prompt (required)
   * @param {Array} templateData.tags - Template tags (optional)
   * @param {boolean} templateData.is_public - Whether template is public (optional)
   * @returns {Promise<Object>} Created template data
   */
  createPromptTemplate: async (templateData) => {
    return handleApiResponse('/api/prompting/templates/', {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(templateData),
    });
  },

  /**
   * Update an existing prompt template
   * @param {number} templateId - Template ID
   * @param {Object} templateData - Updated template data
   * @returns {Promise<Object>} Updated template data
   */
  updatePromptTemplate: async (templateId, templateData) => {
    const url = `/api/prompting/templates/${templateId}/`;
    return handleApiResponse(url, {
      method: 'PUT',
      headers: getAuthHeader(),
      body: JSON.stringify(templateData),
    });
  },

  /**
   * Delete a prompt template
   * @param {number} templateId - Template ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  deletePromptTemplate: async (templateId) => {
    const url = `/api/prompting/templates/${templateId}/`;
    return handleApiResponse(url, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
  },

  /**
   * Get public templates for leaderboard
   * @param {Object} params - Query parameters
   * @param {string} params.method - Filter by method
   * @param {string} params.category - Filter by category
   * @param {string} params.sort_by - Sort field (rating, usage_count, created_at)
   * @param {string} params.order - Sort order (asc, desc)
   * @param {number} params.limit - Number of templates to return
   * @param {number} params.offset - Offset for pagination
   * @returns {Promise<Object>} Public templates response
   */
  getPublicPromptTemplates: async (params = {}) => {
    const queryString = Object.entries(params)
      .filter(([_, value]) => value !== undefined)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');
    const url = `/api/prompting/templates/public/${queryString ? `?${queryString}` : ''}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Get all user tags from templates
   * @returns {Promise<Array>} Array of unique tags
   */
  getUserTags: async () => {
    try {
      const response = await ApiServices.getPromptTemplates({ limit: 1000 });
      if (response.success && response.templates) {
        const allTags = new Set();
        response.templates.forEach(template => {
          if (template.tags && Array.isArray(template.tags)) {
            template.tags.forEach(tag => allTags.add(tag));
          }
        });
        return Array.from(allTags).sort();
      }
      return [];
    } catch (error) {
      console.error('Error fetching user tags:', error);
      return [];
    }
  },

  /**
   * Save a copy of a public template to user's library
   * @param {number} templateId - Template ID to save
   * @returns {Promise<Object>} Saved template data
   */
  saveTemplateCopy: async (templateId) => {
    const url = `/api/prompting/templates/${templateId}/save/`;
    return handleApiResponse(url, {
      method: 'POST',
      headers: getAuthHeader(),
    });
  },

  /**
   * Remove a saved template from user's library
   * @param {number} templateId - Template ID to unsave
   * @returns {Promise<Object>} Unsave confirmation
   */
  unsaveTemplate: async (templateId) => {
    const url = `/api/prompting/templates/${templateId}/unsave/`;
    return handleApiResponse(url, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
  },

  /**
   * Rate a template
   * @param {number} templateId - Template ID
   * @param {number} rating - Rating value (1-5)
   * @returns {Promise<Object>} Rating confirmation
   */
  rateTemplate: async (templateId, rating) => {
    const url = `/api/prompting/templates/${templateId}/rate/`;
    return handleApiResponse(url, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ rating }),
    });
  },

  // ===== CHAT CONVERSATIONS API =====

  /**
   * Get user's chat conversations
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Number of conversations to return (default: 20)
   * @param {number} params.offset - Offset for pagination (default: 0)
   * @returns {Promise<Object>} Conversations response { success, conversations, count }
   */
  getChatConversations: async (params = {}) => {
    const queryString = Object.entries(params)
      .filter(([_, value]) => value !== undefined)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');
    const url = `/api/prompting/chat/conversations/${queryString ? `?${queryString}` : ''}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Create a new chat conversation
   * @param {Object} data - Conversation data
   * @param {string} data.title - Conversation title (optional)
   * @returns {Promise<Object>} Created conversation response { success, conversation, message }
   */
  createChatConversation: async (data = {}) => {
    return handleApiResponse('/api/prompting/chat/conversations/', {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(data),
    });
  },

  /**
   * Get a single chat conversation by ID
   * @param {number} conversationId - Conversation ID
   * @returns {Promise<Object>} Conversation response { success, conversation }
   */
  getChatConversation: async (conversationId) => {
    const url = `/api/prompting/chat/conversations/${conversationId}/`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Update a chat conversation
   * @param {number} conversationId - Conversation ID
   * @param {Object} data - Updated conversation data
   * @param {string} data.title - New title (optional)
   * @returns {Promise<Object>} Updated conversation response { success, conversation, message }
   */
  updateChatConversation: async (conversationId, data = {}) => {
    const url = `/api/prompting/chat/conversations/${conversationId}/`;
    return handleApiResponse(url, {
      method: 'PUT',
      headers: getAuthHeader(),
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a chat conversation
   * @param {number} conversationId - Conversation ID
   * @returns {Promise<Object>} Deletion confirmation { success, message }
   */
  deleteChatConversation: async (conversationId) => {
    const url = `/api/prompting/chat/conversations/${conversationId}/`;
    return handleApiResponse(url, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
  },

  // ===== CHAT MESSAGES API =====

  /**
   * Get messages for a conversation
   * @param {number} conversationId - Conversation ID
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Number of messages to return (default: 50)
   * @param {number} params.offset - Offset for pagination (default: 0)
   * @returns {Promise<Object>} Messages response { success, messages, count }
   */
  getChatMessages: async (conversationId, params = {}) => {
    const queryString = Object.entries(params)
      .filter(([_, value]) => value !== undefined)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');
    const url = `/api/prompting/chat/conversations/${conversationId}/messages/${queryString ? `?${queryString}` : ''}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Send a message to a conversation
   * @param {number} conversationId - Conversation ID
   * @param {Object} messageData - Message data
   * @param {string} messageData.content - Message content (required)
   * @param {number} messageData.template_id - Template ID (optional)
   * @param {string} messageData.model - Model override (optional)
   * @param {number} messageData.temperature - Temperature override (optional)
   * @param {number} messageData.max_tokens - Max tokens override (optional)
   * @param {string} messageData.system_prompt - System prompt override (optional)
   * @param {string} messageData.data_source - Data source ('google-trends', 'keitaro', 'all', 'none') (optional)
   * @param {string} messageData.data_type - Data type ('dataset', 'single') (optional)
   * @param {string} messageData.data_geo - Geographic region code (optional)
   * @param {string} messageData.data_category - Category filter (optional)
   * @param {string} messageData.data_domain - Domain filter (optional)
   * @param {string} messageData.data_date - Date range ('4h', '1day', '2days', '7days') (optional)
   * @param {number} messageData.selected_item_id - Selected item ID for single type (optional)
   * @returns {Promise<Object>} Response { success, user_message, assistant_message, tokens_used }
   */
  sendChatMessage: async (conversationId, messageData) => {
    const url = `/api/prompting/chat/conversations/${conversationId}/messages/`;
    return handleApiResponse(url, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(messageData),
    });
  },

  // ===== CHAT SETTINGS API =====

  /**
   * Get user's chat settings
   * @returns {Promise<Object>} Settings response { success, settings }
   */
  getChatSettings: async () => {
    return handleApiResponse('/api/prompting/chat/settings/', {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Update user's chat settings
   * @param {Object} settings - Settings data
   * @param {string} settings.default_model - Default model (optional)
   * @param {number} settings.default_temperature - Default temperature (optional)
   * @param {number} settings.default_max_tokens - Default max tokens (optional)
   * @param {string} settings.system_prompt - System prompt (optional)
   * @returns {Promise<Object>} Updated settings response { success, settings, message }
   */
  updateChatSettings: async (settings = {}) => {
    return handleApiResponse('/api/prompting/chat/settings/', {
      method: 'PUT',
      headers: getAuthHeader(),
      body: JSON.stringify(settings),
    });
  },

  // ===== SAVE AS TEMPLATE API =====

  /**
   * Save content as a template
   * @param {Object} data - Template data
   * @param {string} data.name - Template name (required)
   * @param {string} data.description - Template description (optional)
   * @param {string} data.content - Content to save as template (required)
   * @param {string} data.method - Prompting method (optional)
   * @param {Array} data.tags - Template tags (optional)
   * @param {boolean} data.is_public - Whether template is public (optional)
   * @param {number} data.message_id - Message ID if saving from message (optional)
   * @returns {Promise<Object>} Created template response { success, template, message }
   */
  saveAsTemplate: async (data) => {
    return handleApiResponse('/api/prompting/chat/save-as-template/', {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(data),
    });
  },

  // ===== KEITARO API =====

  /**
   * Get statistics about Keitaro celebrities and trends
   * @returns {Promise<Object>} Statistics response
   */
  getKeitaroStats: async () => {
    return handleApiResponse('/api/keitaro/stats/', {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Get list of celebrities from Keitaro with pagination and filters
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.page_size - Items per page (default: 20)
   * @param {string} params.country - Filter by country
   * @param {string} params.domain - Filter by domain
   * @param {string} params.search - Search by name
   * @returns {Promise<Object>} Celebrities response with pagination
   */
  getKeitaroCelebrities: async (params = {}) => {
    const queryString = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');
    const url = `/api/keitaro/celebrities/${queryString ? `?${queryString}` : ''}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Get celebrity details by ID
   * @param {number} celebrityId - Celebrity ID
   * @returns {Promise<Object>} Celebrity details
   */
  getKeitaroCelebrityById: async (celebrityId) => {
    const url = `/api/keitaro/celebrities/${celebrityId}/`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Synchronize celebrities from Keitaro API
   * @param {Object} options - Sync options
   * @param {boolean} options.force - Force sync all records
   * @returns {Promise<Object>} Sync result with statistics
   */
  syncKeitaro: async (options = {}) => {
    return handleApiResponse('/api/keitaro/sync/', {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ force: options.force || false }),
    });
  },

  /**
   * Update trend data for celebrities through Google Trends (async via Celery)
   * @param {Object} params - Update parameters
   * @param {string} params.geo - Geographic code (required, e.g., 'US', 'RU')
   * @param {string} params.country - Filter celebrities by country (optional)
   * @param {number} params.limit - Maximum number to process (optional, None = process all)
   * @param {Array<number>} params.celebrity_ids - Specific IDs to update (optional)
   * @returns {Promise<Object>} Response with task_id for tracking progress: { task_id, status, message, geo, country }
   */
  updateKeitaroTrends: async (params) => {
    const queryParams = new URLSearchParams();
    if (params.geo) {
      queryParams.append('geo', params.geo);
    }
    if (params.country) {
      queryParams.append('country', params.country);
    }
    if (params.limit) {
      queryParams.append('limit', params.limit);
    }

    const body = {};
    if (params.celebrity_ids) {
      body.celebrity_ids = params.celebrity_ids;
    }

    const url = `/api/keitaro/update-trends/?${queryParams.toString()}`;
    return handleApiResponse(url, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(body), // Always send a valid JSON object, even if empty
    });
  },

  /**
   * Get trend data for celebrities
   * @param {Object} params - Query parameters
   * @param {string} params.geo - Geographic code (required)
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.page_size - Items per page (default: 20)
   * @param {number} params.min_score - Minimum trend score (optional)
   * @param {boolean} params.has_spike - Filter by spike (optional)
   * @param {string} params.domain - Filter by domain (optional)
   * @returns {Promise<Object>} Trends response with pagination
   */
  getKeitaroTrends: async (params = {}) => {
    const queryString = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');
    const url = `/api/keitaro/trends/${queryString ? `?${queryString}` : ''}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Initialize Keitaro database tables
   * @returns {Promise<Object>} Initialization result
   */
  initKeitaroTables: async () => {
    return handleApiResponse('/api/keitaro/init/', {
      method: 'POST',
      headers: getAuthHeader(),
    });
  },

  // ===== USER MANAGEMENT API =====

  /**
   * Get user permissions
   * @returns {Promise<Object>} Permissions response { success, permissions }
   */
  getUserPermissions: async () => {
    return handleApiResponse('/api/core/users/permissions', {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Get list of users
   * @param {Object} params - Query parameters
   * @param {string} params.role - Filter by role
   * @param {string} params.grade - Filter by grade
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.page_size - Items per page (default: 20)
   * @param {string} params.search - Search by username
   * @returns {Promise<Object>} Users response { success, users, count, page, page_size }
   */
  getUsers: async (params = {}) => {
    const queryString = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');
    const url = `/api/core/users${queryString ? `?${queryString}` : ''}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Get user by ID
   * @param {number} userId - User ID
   * @returns {Promise<Object>} User response { success, user }
   */
  getUserById: async (userId) => {
    const url = `/api/core/users/${userId}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Get current user profile
   * @returns {Promise<Object>} Current user response { success, user }
   */
  getCurrentUser: async () => {
    const url = `/api/core/users/me`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Create a new user
   * @param {Object} userData - User data
   * @param {string} userData.username - Username (required)
   * @param {string} userData.password - Password (required)
   * @param {string} userData.role - Role (ops, mb, promo, admin) (required)
   * @param {string} userData.grade - Grade (lead, employee) (required, except for admin)
   * @returns {Promise<Object>} Created user response { success, user, message }
   */
  createUser: async (userData) => {
    return handleApiResponse('/api/core/users', {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(userData),
    });
  },

  /**
   * Update user
   * @param {number} userId - User ID
   * @param {Object} userData - Updated user data
   * @param {string} userData.username - New username (optional)
   * @param {string} userData.password - New password (optional)
   * @param {string} userData.role - New role (optional, admin only)
   * @param {string} userData.grade - New grade (optional, admin only)
   * @returns {Promise<Object>} Updated user response { success, user, message }
   */
  updateUser: async (userId, userData) => {
    const url = `/api/core/users/${userId}`;
    return handleApiResponse(url, {
      method: 'PUT',
      headers: getAuthHeader(),
      body: JSON.stringify(userData),
    });
  },

  /**
   * Delete user (admin only)
   * @param {number} userId - User ID
   * @returns {Promise<Object>} Deletion confirmation { success, message }
   */
  deleteUser: async (userId) => {
    const url = `/api/core/users/${userId}`;
    return handleApiResponse(url, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
  },

  /**
   * Deactivate user
   * @param {number} userId - User ID
   * @returns {Promise<Object>} Deactivation response { success, user, message }
   */
  deactivateUser: async (userId) => {
    const url = `/api/core/users/${userId}/deactivate`;
    return handleApiResponse(url, {
      method: 'POST',
      headers: getAuthHeader(),
    });
  },

  /**
   * Activate user (admin only)
   * @param {number} userId - User ID
   * @returns {Promise<Object>} Activation response { success, user, message }
   */
  activateUser: async (userId) => {
    const url = `/api/core/users/${userId}/activate`;
    return handleApiResponse(url, {
      method: 'POST',
      headers: getAuthHeader(),
    });
  },

  // ===== STATISTICS API =====

  /**
   * Get overview metrics (KPIs) for statistics dashboard
   * @param {Object} params - Query parameters
   * @param {string} params.geo - Geographic filter (default: 'all')
   * @param {string} params.dateRange - Date range filter ('7d', '30d', '90d')
   * @param {boolean} params.useCache - Whether to use cached data (default: true)
   * @returns {Promise<Object>} Overview metrics
   */
  getStatisticsOverview: async (params = {}) => {
    const queryParams = new URLSearchParams({
      geo: params.geo || 'all',
      date_range: params.dateRange || '7d',
      cache: (params.useCache !== false).toString(),
    });
    const url = `/api/statistics/overview?${queryParams}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Get comparison list with pagination and filtering
   * @param {Object} params - Query parameters
   * @param {string} params.geo - Geographic filter
   * @param {number} params.page - Page number (1-based)
   * @param {number} params.pageSize - Items per page
   * @param {string} params.sortBy - Field to sort by
   * @param {string} params.sortOrder - Sort direction ('asc' or 'desc')
   * @param {Object} params.filters - Additional filters
   * @returns {Promise<Object>} Comparisons with pagination info
   */
  getStatisticsComparisons: async (params = {}) => {
    const queryParams = new URLSearchParams({
      geo: params.geo || 'all',
      page: (params.page || 1).toString(),
      page_size: (params.pageSize || 20).toString(),
      sort_by: params.sortBy || 'trend_score',
      sort_order: params.sortOrder || 'desc',
    });

    // Add filter params
    if (params.filters) {
      Object.entries(params.filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '' && value !== 'all') {
          const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
          queryParams.append(snakeKey, value.toString());
        }
      });
    }

    const url = `/api/statistics/comparisons?${queryParams}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Get correlation analysis results
   * @param {Object} params - Query parameters
   * @param {string} params.geo - Geographic filter
   * @param {string} params.dateRange - Date range filter
   * @param {boolean} params.useCache - Whether to use cached data
   * @returns {Promise<Object>} Correlation results
   */
  getStatisticsCorrelations: async (params = {}) => {
    const queryParams = new URLSearchParams({
      geo: params.geo || 'all',
      date_range: params.dateRange || '7d',
      cache: (params.useCache !== false).toString(),
    });
    const url = `/api/statistics/correlations?${queryParams}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Get active alerts for statistics dashboard
   * @param {Object} params - Query parameters
   * @param {string} params.geo - Geographic filter
   * @returns {Promise<Object>} Alerts grouped by type
   */
  getStatisticsAlerts: async (params = {}) => {
    const queryParams = new URLSearchParams({
      geo: params.geo || 'all',
    });
    const url = `/api/statistics/alerts?${queryParams}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Get scatter plot data for quadrant analysis
   * @param {Object} params - Query parameters
   * @param {string} params.geo - Geographic filter
   * @param {string} params.xMetric - X-axis metric
   * @param {string} params.yMetric - Y-axis metric
   * @returns {Promise<Object>} Scatter plot data with quadrant info
   */
  getStatisticsScatterData: async (params = {}) => {
    const queryParams = new URLSearchParams({
      geo: params.geo || 'all',
      x_metric: params.xMetric || 'trend_score',
      y_metric: params.yMetric || 'cr',
    });
    const url = `/api/statistics/scatter-data?${queryParams}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Get coverage funnel data
   * @param {Object} params - Request parameters
   * @param {string} params.geo - Geographic filter
   * @param {string} params.dateRange - Date range filter ('7d', '30d', '90d')
   * @returns {Promise<Object>} Funnel data with stages and gap analysis
   */
  getStatisticsFunnel: async (params = {}) => {
    const queryParams = new URLSearchParams({
      geo: params.geo || 'all',
      date_range: params.dateRange || '7d',
    });
    const url = `/api/statistics/funnel?${queryParams}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Export statistics data to CSV or Excel
   * @param {Object} params - Export parameters
   * @param {string} params.format - Export format ('csv' or 'excel')
   * @param {string} params.geo - Geographic filter
   * @param {Object} params.filters - Additional filters
   * @returns {Promise<Object>} Object with blob and filename
   */
  exportStatisticsData: async (params = {}) => {
    const response = await fetch('/api/statistics/export', {
      method: 'POST',
      headers: {
        ...API_CONFIG.headers,
        ...getAuthHeader(),
      },
      credentials: 'include',
      body: JSON.stringify({
        format: params.format || 'csv',
        geo: params.geo || 'all',
        filters: params.filters || {},
      }),
    });

    if (!response.ok) {
      let errorMessage = 'Ошибка экспорта';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (e) {
        errorMessage = response.statusText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `statistics_export.${params.format === 'excel' ? 'xlsx' : 'csv'}`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename=(.+)/);
      if (match) {
        filename = match[1].replace(/"/g, '');
      }
    }

    const blob = await response.blob();
    return { blob, filename };
  },

  /**
   * Start manual Keitaro synchronization for statistics
   * @param {Object} params - Sync parameters
   * @param {string} params.dateFrom - Start date (ISO format)
   * @param {string} params.dateTo - End date (ISO format)
   * @param {string} params.geo - Geographic filter (optional)
   * @returns {Promise<Object>} Sync task info
   */
  syncStatisticsKeitaro: async (params = {}) => {
    if (!params.dateFrom || !params.dateTo) {
      throw new Error('dateFrom и dateTo обязательны');
    }
    return handleApiResponse('/api/statistics/keitaro-sync', {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({
        date_from: params.dateFrom,
        date_to: params.dateTo,
        geo: params.geo || null,
      }),
    });
  },

  /**
   * Get statistics sync status
   * @param {string} taskId - Task ID (required, returned from syncStatisticsKeitaro)
   * @returns {Promise<Object>} Task status object
   */
  getStatisticsSyncStatus: async (taskId) => {
    if (!taskId) {
      throw new Error('task_id is required');
    }
    const url = `/api/statistics/sync-status/${taskId}/`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Invalidate statistics cache
   * @param {Object} params - Parameters
   * @param {string} params.geo - Geographic filter (null = invalidate all)
   * @returns {Promise<Object>} Result
   */
  invalidateStatisticsCache: async (params = {}) => {
    return handleApiResponse('/api/statistics/invalidate-cache', {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ geo: params.geo || null }),
    });
  },

  /**
   * Start data fetching process for statistics
   * @param {Object} params - Request parameters
   * @param {Array<string>} params.geo - Array of geographic codes (1-5 countries, cannot be "all")
   * @param {string} params.date_range - Date range for Google Trends ('4h', '1day', '2days', '7days')
   * @returns {Promise<Object>} Response with task_id for tracking progress
   */
  fetchStatisticsData: async (params = {}) => {
    if (!params.geo || !Array.isArray(params.geo) || params.geo.length === 0) {
      throw new Error('geo must be a non-empty array');
    }
    if (params.geo.length > 5) {
      throw new Error('geo must contain 1-5 country codes');
    }
    if (params.geo.includes('all')) {
      throw new Error('geo cannot contain "all", must specify individual countries');
    }
    if (!params.date_range || !['4h', '1day', '2days', '7days'].includes(params.date_range)) {
      throw new Error('date_range must be one of: "4h", "1day", "2days", "7days"');
    }

    return handleApiResponse('/api/statistics/fetch-data', {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({
        geo: params.geo,
        date_range: params.date_range,
      }),
    });
  },

  /**
   * Get statistics data fetch status
   * @param {string} taskId - Task ID (required, returned from fetchStatisticsData)
   * @returns {Promise<Object>} Task status object with all sub_tasks and stages
   */
  getStatisticsFetchStatus: async (taskId) => {
    if (!taskId) {
      throw new Error('task_id is required');
    }
    const url = `/api/statistics/fetch-data-status/${taskId}/`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * News related API services
   * Get latest news articles from the backend with filtering and pagination
   * @param {Object} params - Query parameters (limit, offset, geo, category, tags, search)
   * @returns {Promise<Array>} List of news articles
   */
  getLatestNews: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    if (params.geo && params.geo !== 'all') queryParams.append('geo', params.geo);
    if (params.category && params.category !== 'all') queryParams.append('category', params.category);
    if (params.search) queryParams.append('search', params.search);
    if (params.tags && params.tags.length > 0) {
      params.tags.forEach(tag => queryParams.append('tags', tag));
    }
    if (params.only_with_correlation) queryParams.append('only_with_correlation', '1');

    const url = `/api/news/?${queryParams.toString()}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Get hot news for the HomePage (latest 12, no filters)
   * @param {Object} params - Request parameters
   * @param {string} params.geo - Geographic filter ('all' or specific country code)
   * @returns {Promise<Array>} List of hot news articles
   */
  getHotNews: async (params = {}) => {
    const geo = params.geo === 'all' ? null : params.geo;
    const url = geo ? `/api/news/hot/?geo=${geo}` : '/api/news/hot/';
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Find news articles correlated with a trend (same GEO + trend title in title/description)
   * @param {string} trend - The trend query string
   * @param {string} geo - Geographic region code
   * @param {number} limit - Max number of results (default 5)
   * @returns {Promise<Array>} List of correlated news articles
   */
  getCorrelatedNews: async (trend, geo, limit = 5) => {
    const queryParams = new URLSearchParams();
    queryParams.append('trend', trend);
    queryParams.append('geo', geo);
    queryParams.append('limit', limit);
    const url = `/api/news/correlated/?${queryParams.toString()}`;
    return handleApiResponse(url, {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  // ===== NOTIFICATIONS API =====

  /**
   * Get current user's notification settings
   * @returns {Promise<Object>} Notification settings
   */
  getNotificationSettings: async () => {
    return handleApiResponse('/api/notifications/', {
      method: 'GET',
      headers: getAuthHeader(),
    });
  },

  /**
   * Update notification settings (geos, tags, content types, enabled)
   * @param {Object} data - { geos: string[], tags: string[], content_types?: ('news'|'trends'|'celebs')[], enabled?: boolean }
   * @returns {Promise<Object>} Updated settings
   */
  updateNotificationSettings: async (data) => {
    return handleApiResponse('/api/notifications/', {
      method: 'PUT',
      headers: getAuthHeader(),
      body: JSON.stringify(data),
    });
  },

  /**
   * Toggle notifications enabled/disabled
   * @returns {Promise<Object>} { enabled: boolean }
   */
  toggleNotifications: async () => {
    return handleApiResponse('/api/notifications/toggle', {
      method: 'POST',
      headers: getAuthHeader(),
    });
  },

  /**
   * Generate a Telegram deep-link for account binding
   * @returns {Promise<Object>} { deep_link, token, expires_at }
   */
  linkTelegram: async () => {
    return handleApiResponse('/api/notifications/link', {
      method: 'POST',
      headers: getAuthHeader(),
    });
  },

  /**
   * Remove Telegram binding
   * @returns {Promise<Object>} { message }
   */
  unlinkTelegram: async () => {
    return handleApiResponse('/api/notifications/unlink', {
      method: 'POST',
      headers: getAuthHeader(),
    });
  },

  /**
   * Get available geos and tags for notification configuration
   * @returns {Promise<Object>} { geos: string[], tags: string[] }
   */
  getNotificationOptions: async () => {
    return handleApiResponse('/api/notifications/options', {
      method: 'GET',
      headers: getAuthHeader(),
    });
  }
};

/**
 * Export newsService as an alias for backward compatibility
 */
export const newsService = ApiServices;

/**
 * Download file helper for statistics export
 * @param {Blob} blob - File blob
 * @param {string} filename - File name
 */
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};